import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Copy, CheckCircle2, Loader2, ExternalLink, Smartphone, Building2,
    MessageCircle, Upload, ShieldCheck, AlertCircle, IndianRupee, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { SOCIAL } from "../data/siteData";

/**
 * V124 — Public Dynamic Payment Link page.
 *
 * Route: /pay/:linkId
 *
 * Product decisions:
 *   • NO T&C consent gate — parent lands directly on the payment page.
 *   • Amount displayed prominently, cannot be edited on our side. The Razorpay
 *     link opens razorpay.me/@handle where the parent enters the amount
 *     manually (trust-based; see backend/payment_links.py header for the
 *     rationale + admin-decided fallback).
 *   • Screenshot upload is 100% optional; the "Payment Successful" confirmation
 *     screen never blocks on the upload finishing.
 *   • Mobile-first — all CTAs are single-column full-width on <640px.
 */
export default function PublicPaymentLink() {
    const { linkId } = useParams();
    const [state, setState] = useState({ loading: true, link: null, error: null });
    const [confirmed, setConfirmed] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);
    const fileInputRef = useRef(null);

    const API = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/api/payment/link/${linkId}`);
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    if (!cancelled) setState({ loading: false, link: null, error: body.detail || `error_${res.status}` });
                    return;
                }
                const link = await res.json();
                if (!cancelled) setState({ loading: false, link, error: null });
            } catch (e) {
                if (!cancelled) setState({ loading: false, link: null, error: "network_error" });
            }
        })();
        return () => { cancelled = true; };
    }, [API, linkId]);

    const copy = async (val, label) => {
        try {
            await navigator.clipboard.writeText(val);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Could not copy — please long-press to select and copy manually.");
        }
    };

    const handleMarkPaid = async () => {
        try {
            await fetch(`${API}/api/payment/link/${linkId}/confirm`, { method: "POST" });
        } catch {
            // silent — soft signal only
        }
        setConfirmed(true);
    };

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await fetch(`${API}/api/payment/link/${linkId}/screenshot`, {
                method: "POST",
                body: form,
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || `HTTP ${res.status}`);
            }
            setUploadDone(true);
            toast.success("Screenshot uploaded — Anand Tutorials will verify shortly.");
        } catch (e) {
            toast.error(`Upload failed: ${e.message || "please try again"}`);
        } finally {
            setUploading(false);
        }
    };

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
            </div>
        );
    }

    if (state.error || !state.link) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <SEO title="Payment Link" path={`/pay/${linkId}`} noindex />
                <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 text-center" data-testid="pay-link-error">
                    <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                    <h1 className="font-heading text-xl font-bold text-[#0F172A] mb-2">
                        {state.error === "link_cancelled" ? "This payment link has been cancelled." :
                         state.error === "link_not_found" ? "Payment link not found." :
                         "We couldn't load this payment link."}
                    </h1>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">
                        Please check the link with Anand Tutorials, or contact us on WhatsApp for a fresh link.
                    </p>
                    <a
                        href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi, my payment link (${linkId}) is not working. Could you please resend it?`)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-5 py-2.5 rounded-lg text-sm"
                        data-testid="pay-link-error-whatsapp"
                    >
                        <MessageCircle className="w-4 h-4" /> Message us on WhatsApp
                    </a>
                    <div className="mt-6 text-xs text-slate-400">Reference: {linkId}</div>
                </div>
            </div>
        );
    }

    const { link } = state;
    const acct = link.account || {};
    const amountLabel = new Intl.NumberFormat("en-IN", { style: "currency", currency: link.currency || "INR", maximumFractionDigits: 2 }).format(link.amount);
    const amountRaw = String(link.amount);
    const isPaid = link.status === "paid";

    // UPI deep link (opens native UPI app on Android; iOS: PhonePe / GPay app schemes vary)
    const upiPayeeName = encodeURIComponent(acct.account_holder || "Anand Tutorials");
    const upiTxnNote = encodeURIComponent(link.description || `Fee for ${link.student_name || "student"}`);
    const upiDeepLink = acct.upi_id
        ? `upi://pay?pa=${encodeURIComponent(acct.upi_id)}&pn=${upiPayeeName}&am=${amountRaw}&cu=INR&tn=${upiTxnNote}`
        : null;

    const razorpayUrl = acct.razorpay_handle ? `https://razorpay.me/@${acct.razorpay_handle}` : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30" data-testid="pay-link-page">
            <SEO title={`Pay ${amountLabel} — Anand Tutorials`} path={`/pay/${link.id}`} noindex />

            {/* Header strip — logo + secure badge */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="pay-link-home">
                        <img src="/logo-anand-tutorials.png" alt="Anand Tutorials" className="h-7 w-auto" />
                        <span className="font-heading font-bold text-[#0F172A] text-sm hidden sm:inline">Anand Tutorials</span>
                    </Link>
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                        <ShieldCheck className="w-3 h-3" /> Secure Payment
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-6 sm:py-10 pb-24 space-y-6">

                {/* If confirmed OR admin has marked paid → confirmation screen */}
                {(confirmed || isPaid) ? (
                    <ConfirmationCard link={link} amountLabel={amountLabel} uploadDone={uploadDone} />
                ) : (
                    <>
                        {/* Amount card */}
                        <div className="bg-white rounded-2xl border-2 border-amber-300 shadow-lg p-6 sm:p-8 text-center relative overflow-hidden" data-testid="pay-link-amount-card">
                            <div className="absolute inset-x-0 -top-20 h-40 bg-amber-100/60 blur-3xl pointer-events-none" />
                            <div className="relative">
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-700 mb-1">Amount to pay</div>
                                <div className="font-heading text-4xl sm:text-5xl font-black text-[#0F172A] tabular-nums flex items-center justify-center" data-testid="pay-link-amount">
                                    <IndianRupee className="w-7 sm:w-9 h-7 sm:h-9 -mr-1" />
                                    {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(link.amount)}
                                </div>
                                {(link.student_name || link.parent_name || link.description || link.session_count) && (
                                    <div className="mt-4 text-sm text-slate-600 leading-relaxed space-y-0.5" data-testid="pay-link-meta">
                                        {link.student_name && <div><span className="text-slate-400 uppercase text-[10px] tracking-widest mr-2">Student</span>{link.student_name}</div>}
                                        {link.parent_name && <div><span className="text-slate-400 uppercase text-[10px] tracking-widest mr-2">Parent</span>{link.parent_name}</div>}
                                        {link.session_count && <div data-testid="pay-link-sessions"><span className="text-slate-400 uppercase text-[10px] tracking-widest mr-2">Sessions</span>{link.session_count}</div>}
                                        {link.description && <div className="italic">{link.description}</div>}
                                    </div>
                                )}
                                <div className="mt-4 text-[10px] text-slate-400 uppercase tracking-widest">Ref · {link.id}</div>
                            </div>
                        </div>

                        {/* Payment method cards */}
                        <div className="space-y-4">
                            {/* UPI QR — opens native UPI app on mobile */}
                            {upiDeepLink && (
                                <a
                                    href={upiDeepLink}
                                    className="block group bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] text-white rounded-2xl shadow-lg p-5 sm:p-6 hover:shadow-xl active:scale-[0.995] transition-all"
                                    data-testid="pay-link-upi-cta"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                                            <Smartphone className="w-6 h-6 text-amber-300" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300 mb-1">UPI · GPay / PhonePe / Paytm / BHIM</div>
                                            <div className="font-heading font-bold text-lg sm:text-xl leading-snug">Pay {amountLabel} via UPI</div>
                                            <div className="text-blue-100 text-xs sm:text-sm mt-1">Tap to open your UPI app with the amount pre-filled.</div>
                                        </div>
                                        <ExternalLink className="w-5 h-5 opacity-70 shrink-0 mt-1" />
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                            <div className="text-blue-200 text-[10px] uppercase tracking-widest">UPI ID</div>
                                            <div className="font-mono font-semibold">{acct.upi_id}</div>
                                        </div>
                                        <div>
                                            <div className="text-blue-200 text-[10px] uppercase tracking-widest">UPI Phone</div>
                                            <div className="font-mono font-semibold">{acct.upi_phone || "—"}</div>
                                        </div>
                                    </div>
                                </a>
                            )}

                            {/* Razorpay — opens razorpay.me handle in new tab */}
                            {razorpayUrl && (
                                <div className="bg-white rounded-2xl border border-blue-100 shadow-md p-5 sm:p-6" data-testid="pay-link-razorpay-card">
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                                            <span className="text-2xl">💳</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1E3A8A] mb-1">Card · Net Banking · Wallet · UPI</div>
                                            <div className="font-heading font-bold text-lg text-[#0F172A] leading-snug">Pay via Razorpay</div>
                                            <div className="text-slate-500 text-xs sm:text-sm mt-1">
                                                Please enter <strong className="text-[#0F172A] tabular-nums">{amountLabel}</strong> as the amount on the Razorpay page.
                                            </div>
                                        </div>
                                    </div>
                                    <a
                                        href={razorpayUrl}
                                        target="_blank" rel="noopener noreferrer"
                                        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] hover:from-[#1E40AF] hover:to-[#1D4ED8] text-white font-semibold px-5 py-3 rounded-lg text-sm shadow-md active:scale-[0.98] transition-all"
                                        data-testid="pay-link-razorpay-btn"
                                    >
                                        Pay via Razorpay <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                            )}

                            {/* Bank transfer */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="pay-link-bank-card">
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                                        <Building2 className="w-6 h-6 text-[#0F172A]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0F172A] mb-1">Bank Transfer · NEFT / IMPS / RTGS</div>
                                        <div className="font-heading font-bold text-lg text-[#0F172A] leading-snug">Direct to {acct.bank_name || acct.label}</div>
                                    </div>
                                </div>
                                <dl className="mt-4 space-y-3">
                                    <Row label="Account Holder" value={acct.account_holder} onCopy={() => copy(acct.account_holder, "Account holder")} />
                                    <Row label="Account Number" value={acct.account_number} mono onCopy={() => copy(acct.account_number, "Account number")} />
                                    <Row label="IFSC Code" value={acct.ifsc} mono onCopy={() => copy(acct.ifsc, "IFSC")} />
                                    <Row label="Bank" value={acct.bank_name} />
                                </dl>
                            </div>
                        </div>

                        {/* "I have paid" primary confirmation */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 sm:p-6" data-testid="pay-link-confirm-block">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-heading font-bold text-[#0F172A] mb-1">Once you have paid</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Tap the button below to confirm. You can optionally attach a screenshot afterwards.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleMarkPaid}
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-lg text-sm shadow active:scale-[0.98] transition-all"
                                data-testid="pay-link-mark-paid-btn"
                            >
                                <CheckCircle2 className="w-4 h-4" /> I have paid {amountLabel}
                            </button>
                        </div>
                    </>
                )}

                {/* Optional screenshot upload — shown in both states */}
                {!isPaid && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="pay-link-screenshot-block">
                        <div className="flex items-start gap-3 mb-3">
                            <ImageIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-heading font-bold text-[#0F172A]">Payment screenshot <span className="text-xs font-normal text-slate-400 tracking-wide uppercase">(optional)</span></h3>
                                <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                                    Screenshot sharing is optional. You may also send it directly to Anand Tutorials on WhatsApp.
                                </p>
                            </div>
                        </div>
                        {uploadDone ? (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" /> Screenshot uploaded. We&apos;ll verify shortly.
                            </div>
                        ) : (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => handleUpload(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="pay-link-file-input"
                                />
                                <div className="grid sm:grid-cols-2 gap-2">
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-[#1E3A8A] text-[#0F172A] font-semibold px-4 py-2.5 rounded-lg text-sm disabled:opacity-60"
                                        data-testid="pay-link-upload-btn"
                                    >
                                        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload screenshot</>}
                                    </button>
                                    <a
                                        href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi, I have paid ${amountLabel} for ref ${link.id}. Screenshot attached.`)}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-4 py-2.5 rounded-lg text-sm"
                                        data-testid="pay-link-whatsapp-btn"
                                    >
                                        <MessageCircle className="w-4 h-4" /> Send on WhatsApp
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Trust footer */}
                <div className="text-center text-xs text-slate-400 leading-relaxed">
                    Powered by Anand Tutorials · Since 2013 · 50,000+ students taught pan-India
                    <br />
                    Payments are handled directly by your chosen bank / UPI / Razorpay — Anand Tutorials never sees your card or UPI PIN.
                </div>
            </main>
        </div>
    );
}

/* -------------------------------------------------------------------------- */

function Row({ label, value, mono, onCopy }) {
    if (!value) return null;
    return (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
                <div className={`font-semibold text-[#0F172A] text-sm sm:text-base break-all ${mono ? "font-mono" : ""}`}>{value}</div>
            </div>
            {onCopy && (
                <button
                    onClick={onCopy}
                    className="shrink-0 p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                    aria-label={`Copy ${label}`}
                    data-testid={`pay-link-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <Copy className="w-4 h-4 text-slate-600" />
                </button>
            )}
        </div>
    );
}

function ConfirmationCard({ link, amountLabel, uploadDone }) {
    return (
        <div className="bg-white rounded-2xl border-2 border-emerald-300 shadow-lg p-6 sm:p-10 text-center" data-testid="pay-link-confirmation">
            <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
                <CheckCircle2 className="w-9 h-9" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-2">Payment completed successfully.</h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-lg mx-auto mb-4">
                Thank you! We&apos;ll verify the {amountLabel} payment against ref <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{link.id}</code> and confirm on WhatsApp within one working day.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5 text-left mt-6 mb-6">
                <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700 leading-relaxed">
                        You may share your payment screenshot with Anand Tutorials on WhatsApp for confirmation.
                        <strong className="text-[#0F172A]"> Screenshot sharing is optional.</strong>
                    </p>
                </div>
            </div>
            <a
                href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi, I have paid ${amountLabel} for ref ${link.id}.${uploadDone ? " Screenshot already uploaded on the payment page." : ""}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-6 py-3 rounded-lg text-sm shadow-md"
                data-testid="pay-link-confirmation-whatsapp"
            >
                <MessageCircle className="w-4 h-4" /> Open WhatsApp chat
            </a>
        </div>
    );
}
